/**
 * AvailableStudents - "Available students": how many, and at what
 * ----------------------------------------------------------------
 * What the "Available students" tab shows since 17 October 2026, when the
 * owner archived offers (OFFERS_ENABLED in backend/config/features.js). It
 * used to be a list of students who had published when they were free; now
 * it is numbers:
 *
 *   - how many students are ready to complete chores, in total
 *   - how many are ready for each kind of chore - what they ticked under
 *     "What are you happy to do?" on their profile
 *
 * NUMBERS, AND NEVER NAMES. The list it replaced put a child's name, photo
 * and timetable in front of any adult with an account; that is the part the
 * owner took away, and this page must not quietly bring it back. Every figure
 * here is a count of people. backend/routes/students.js decides who counts as
 * "ready" (an open account a parent has agreed to, where one has to) and why.
 *
 * Posters only, like the list before it - the server refuses anybody else.
 *
 * THE COMPLETED-CHORES TOTAL appears at the bottom only when the server sends
 * it: always for a moderator, with a note that nobody else can see it yet,
 * and for everybody once the owner switches SHOW_CHORES_COMPLETED on.
 *
 * Phone first. The tiles are the same ones as the dashboards' (`.stat`), two
 * to a row on a phone and a tablet, five on a computer - ten kinds of chore
 * divide evenly into both, so no row is left with a gap. See `.skill-counts`
 * in styles/components.css.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { studentApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../hooks/useI18n';
import Loader from '../components/Loader';

export default function AvailableStudents() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    studentApi
      .counts()
      .then((data) => {
        if (active) setCounts(data);
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="container section">
      <div className="page-header">
        <h1>{t('availableStudents.title')}</h1>
        <p>{t('availableStudents.subtitle')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!counts && !error && <Loader label={t('availableStudents.loading')} />}

      {counts && (
        <>
          {/* ---------------- THE TOTAL ---------------- */}
          <div className="stat stat-green ready-total">
            <div className="stat-value">{counts.ready}</div>
            <div className="stat-label">{t('availableStudents.readyTotal')}</div>
          </div>

          {/* ---------------- ONE TILE PER KIND OF CHORE ----------------
              The category is the stored English value and only the label is
              translated - the rule at the top of i18n/en.js. The server sends
              all ten in the site's usual order, with 0 where nobody has
              ticked one: a missing tile would look like a bug. */}
          <h2 className="ready-heading">{t('availableStudents.byKind')}</h2>
          <div className="stat-grid skill-counts">
            {counts.bySkill.map(({ category, count }) => (
              <div className="stat stat-blue" key={category}>
                <div className="stat-value">{count}</div>
                <div className="stat-label">{t(`availableStudents.readyFor.${category}`)}</div>
              </div>
            ))}
          </div>

          {/* ---------------- CHORES COMPLETED, ONCE IT IS PUBLIC ---------------- */}
          {typeof counts.choresCompleted === 'number' && (
            <div className="stat stat-amber chores-done-total">
              <div className="stat-value">{counts.choresCompleted}</div>
              <div className="stat-label">{t('availableStudents.choresCompleted')}</div>
              {!counts.choresCompletedPublic && (
                <p className="text-xs text-muted chores-done-note">
                  {t('availableStudents.onlyModerators')}
                </p>
              )}
            </div>
          )}

          {/* What a poster can actually DO with these numbers. Not for a
              moderator, for the same reason the navbar hides "Post a chore"
              from them - see components/Navbar.jsx. */}
          {!user?.isModerator && (
            <div className="ready-cta">
              <p className="text-muted">{t('availableStudents.ctaBody')}</p>
              {/* No `btn-inline`: on a phone the one thing to do on the page
                  gets the full width, as the other main actions do. */}
              <Link to="/chores/new" className="btn btn-primary">
                {t('dashboard.postChore')}
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
